// Copyright (c) 2026 Goffy. All Rights Reserved. This file belongs to Goffy.

/**
 * A `mysteryWinner` hole event, in either casing the round APIs return.
 *
 * The winner is the player the event is *on*. Its `eventValue` is a bare subgame id — the hole's
 * designated challenge, or the generic prize id the score popup uses when the hole has none — so
 * it never names the winner and is not read here.
 */
export type MysteryWinnerEventLike = {
  playerId?: string;
  PlayerId?: string;
  holeNumber?: number;
  HoleNumber?: number;
  eventType?: string;
  EventType?: string;
  occurredAt?: string | null;
  OccurredAt?: string | null;
};

/**
 * The winning player per hole.
 *
 * A hole pays exactly one mystery prize: the setup screen lets a single player claim it, so its
 * designated challenge is the hole's prize. A hole can still carry awards under two ids (the
 * challenge and the generic prize), so the latest event wins the hole regardless of which id it
 * was recorded against — the same rule the server applies in `MysteryGameCalculator`.
 */
export const resolveMysteryWinnersByHole = (
  holeEvents: readonly MysteryWinnerEventLike[]
): Map<number, string> => {
  const latestByHole = new Map<number, { playerId: string; occurredAt: number }>();

  holeEvents.forEach((event) => {
    const eventType = (event.eventType ?? event.EventType ?? '').toLowerCase();
    if (!eventType.includes('mystery')) return;

    const playerId = event.playerId ?? event.PlayerId;
    const holeNumber = event.holeNumber ?? event.HoleNumber ?? 0;
    if (!playerId || holeNumber <= 0) return;

    const parsed = Date.parse(event.occurredAt ?? event.OccurredAt ?? '');
    const occurredAt = Number.isNaN(parsed) ? 0 : parsed;

    const current = latestByHole.get(holeNumber);
    // Ties keep the later entry, matching the array order the API returns.
    if (current && current.occurredAt > occurredAt) return;
    latestByHole.set(holeNumber, { playerId, occurredAt });
  });

  return new Map(Array.from(latestByHole, ([hole, winner]) => [hole, winner.playerId]));
};
